/**
 * @fileoverview Entity data model for Multi-Entity Financial Platform
 * Defines the structure and validation for entities (personal, business, partnerships, LLCs)
 */

/**
 * @typedef {Object} Entity
 * @property {string} id - Firestore document ID
 * @property {string} name - Entity name (e.g., "Personal", "Catalán Rentals SP")
 * @property {'personal'|'partnership'|'business'|'llc'} type - Entity type
 * @property {string} currency - ISO currency code (e.g., "CLP", "USD")
 * @property {string} [description] - Optional entity description
 * @property {'active'|'inactive'} status - Entity status
 * @property {string} ownerId - Reference to the user who owns this entity
 * @property {Date} createdAt - Entity creation timestamp
 * @property {Date} updatedAt - Last update timestamp
 */

/**
 * @typedef {Object} EntityFormData
 * @property {string} name
 * @property {'personal'|'partnership'|'business'|'llc'} type
 * @property {string} currency
 * @property {string} [description]
 * @property {'active'|'inactive'} [status]
 */

// Entity type definitions
export const ENTITY_TYPES = {
  PERSONAL: 'personal',
  PARTNERSHIP: 'partnership', 
  BUSINESS: 'business',
  LLC: 'llc'
};

// Entity status definitions
export const ENTITY_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive'
};

// Supported currencies (can be expanded)
export const SUPPORTED_CURRENCIES = {
  USD: { code: 'USD', symbol: '$', name: 'US Dollar' },
  CLP: { code: 'CLP', symbol: '$', name: 'Chilean Peso' },
  EUR: { code: 'EUR', symbol: '€', name: 'Euro' },
  GBP: { code: 'GBP', symbol: '£', name: 'British Pound' }
};

/**
 * Creates a new entity object with default values
 * @param {EntityFormData} formData - Entity form data
 * @param {string} ownerId - User ID who owns this entity
 * @returns {Omit<Entity, 'id'>} New entity object without ID
 */
export function createEntity(formData, ownerId) {
  const now = new Date();
  
  return {
    name: formData.name.trim(),
    type: formData.type,
    currency: formData.currency,
    description: formData.description?.trim() || '',
    status: formData.status || ENTITY_STATUS.ACTIVE,
    ownerId,
    createdAt: now,
    updatedAt: now
  };
}

/**
 * Updates an existing entity with new data
 * @param {Entity} existingEntity - Current entity data
 * @param {Partial<EntityFormData>} updates - Fields to update
 * @returns {Entity} Updated entity object
 */
export function updateEntity(existingEntity, updates) {
  return {
    ...existingEntity,
    ...Object.fromEntries(
      Object.entries(updates)
        .filter(([_, value]) => value !== undefined)
        .map(([key, value]) => [key, typeof value === 'string' ? value.trim() : value])
    ),
    updatedAt: new Date()
  };
}

/**
 * Validates entity form data
 * @param {EntityFormData} formData - Data to validate
 * @returns {{isValid: boolean, errors: string[]}} Validation result
 */
export function validateEntity(formData) {
  const errors = [];

  // Validate name
  if (!formData.name || formData.name.trim().length < 2) {
    errors.push('Entity name must be at least 2 characters long');
  }
  if (formData.name && formData.name.trim().length > 100) {
    errors.push('Entity name must be less than 100 characters');
  }

  // Validate type
  if (!formData.type || !Object.values(ENTITY_TYPES).includes(formData.type)) {
    errors.push('Please select a valid entity type');
  }

  // Validate currency
  if (!formData.currency || !Object.keys(SUPPORTED_CURRENCIES).includes(formData.currency)) {
    errors.push('Please select a valid currency');
  }

  // Validate description (optional)
  if (formData.description && formData.description.trim().length > 500) {
    errors.push('Description must be less than 500 characters');
  }

  // Validate status (optional)
  if (formData.status && !Object.values(ENTITY_STATUS).includes(formData.status)) {
    errors.push('Please select a valid status');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Gets display information for an entity type
 * @param {'personal'|'partnership'|'business'|'llc'} type - Entity type
 * @returns {{icon: string, label: string, description: string}} Display info
 */
export function getEntityTypeInfo(type) {
  const typeInfo = {
    [ENTITY_TYPES.PERSONAL]: {
      icon: '👤',
      label: 'Personal',
      description: 'Individual personal finances'
    },
    [ENTITY_TYPES.PARTNERSHIP]: {
      icon: '🤝',
      label: 'Partnership',
      description: 'Business partnership or joint venture'
    },
    [ENTITY_TYPES.BUSINESS]: {
      icon: '🏢',
      label: 'Business',
      description: 'Corporate or business entity'
    },
    [ENTITY_TYPES.LLC]: {
      icon: '🏛️',
      label: 'LLC',
      description: 'Limited Liability Company'
    }
  };

  return typeInfo[type] || { icon: '❓', label: 'Unknown', description: 'Unknown entity type' };
}

/**
 * Gets currency display information
 * @param {string} currencyCode - ISO currency code
 * @returns {{code: string, symbol: string, name: string}} Currency info
 */
export function getCurrencyInfo(currencyCode) {
  return SUPPORTED_CURRENCIES[currencyCode] || { 
    code: currencyCode, 
    symbol: currencyCode, 
    name: 'Unknown Currency' 
  };
}

/**
 * Formats currency amount with entity's currency
 * @param {number} amount - Amount to format
 * @param {Entity} entity - Entity with currency info
 * @returns {string} Formatted currency string
 */
export function formatEntityCurrency(amount, entity) {
  const currencyInfo = getCurrencyInfo(entity.currency);
  
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: entity.currency
    }).format(amount);
  } catch (error) {
    // Fallback if currency is not supported by Intl
    return `${currencyInfo.symbol}${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
}

/**
 * Generates a default entity name based on type
 * @param {'personal'|'partnership'|'business'|'llc'} type - Entity type
 * @param {number} [count=1] - Count for numbering (e.g., "Business 2")
 * @returns {string} Default entity name
 */
export function generateDefaultEntityName(type, count = 1) {
  const baseName = getEntityTypeInfo(type).label;
  return count > 1 ? `${baseName} ${count}` : baseName;
}

/**
 * Checks if an entity can be deleted
 * @param {Entity} entity - Entity to check
 * @returns {{canDelete: boolean, reason?: string}} Deletion eligibility
 */
export function canDeleteEntity(entity) {
  // Personal entities cannot be deleted (always need at least one)
  if (entity.type === ENTITY_TYPES.PERSONAL) {
    return {
      canDelete: false,
      reason: 'Personal entities cannot be deleted'
    };
  }

  // Active entities with transactions should show warning but allow deletion
  if (entity.status === ENTITY_STATUS.ACTIVE) {
    return {
      canDelete: true,
      reason: 'This will also delete all associated transactions and data'
    };
  }

  return { canDelete: true };
}

const EntityAPI = {
  ENTITY_TYPES,
  ENTITY_STATUS,
  SUPPORTED_CURRENCIES,
  createEntity,
  updateEntity,
  validateEntity,
  getEntityTypeInfo,
  getCurrencyInfo,
  formatEntityCurrency,
  generateDefaultEntityName,
  canDeleteEntity
};

export default EntityAPI;